import { FC, useState, useEffect, useCallback, Fragment } from 'react';
import cn from 'classnames';
import { v4 as uuid } from 'uuid';
import { NavLink, useLocation } from 'react-router-dom';
import { Collapse, Text, Drawer } from '@geist-ui/core';
import useAuth from '../../hooks/useAuth';
import products from 'data/products';
import useIsMobile from '../../hooks/useIsMobile';
import { categories } from '../../pages/legacy';
import { ProductsEnum, ProductType } from '../../@types/products';

type ProductHeaderItemsType = Array<{ id: string; key: ProductsEnum } & ProductType>;
type HeaderItemType = { id: string; label: string; href: string | null };
type HeaderItemsType = Array<{ items?: ProductHeaderItemsType } & HeaderItemType>;

const headerItems = (isLoggedIn: boolean): HeaderItemsType => [
  { id: uuid(), label: 'ACASĂ', href: '/' },
  { id: uuid(), label: 'DESPRE NOI', href: '/about' },
  {
    id: uuid(),
    label: isLoggedIn ? 'PRODUSELE MELE' : 'PRODUSELE MELE',
    href: isLoggedIn ? null : null, // STRIPE_DISABLED — era '/pricing'
    items: Object.entries(products).map(([key, value]) => ({
      id: uuid(),
      key: key as ProductsEnum,
      ...value,
    })),
  },
  { id: uuid(), label: 'CONTACT', href: '/contact' },
];

const EUBanner: FC = () => (
  <div className="header__eu-banner">
    <div className="container">
      <div className="header__eu-banner-inner">
        <img
          src="/assets/eu/pnrr-banner.png"
          alt="Finanțat de Uniunea Europeană — NextGenerationEU | Guvernul României | Planul Național de Redresare și Reziliență"
          className="header__eu-banner-img"
        />
      </div>
    </div>
  </div>
);

const HeaderPart: FC<{ onClick?: () => void }> = ({ onClick }) => (
  <div className="header__part" onClick={onClick}>
    <NavLink to="/" className="header__logo-link">
      <img src="/assets/logo.png" alt="header logo" />
    </NavLink>
  </div>
);

const MobileMenu: FC<{ items: HeaderItemsType }> = ({ items = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isAuthenticated, logout } = useAuth();
  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  return (
    <div className="header__part-mobile">
      <button type="button" onClick={handleOpen} className="icon-button">
        <img alt="header burger icon" className="header__mobile-burger" src="/assets/icons/menu-icon.svg" />
      </button>
      <Drawer visible={isOpen} onClose={handleClose} placement="right" id="burger_container">
        <Drawer.Content className="burger_content">
          <HeaderPart onClick={handleClose} />
          <div className="authentication">
            {isAuthenticated ? (
              <>
                <NavLink to="/profile" className="profile" onClick={handleClose}>
                  <img src="/assets/icons/profil_icon.svg" alt="profile icon" />
                </NavLink>
                <button type="button" className="button log_out" onClick={logout}>Deconectare</button>
              </>
            ) : (
              <>
                <NavLink to="/auth/login" className="log_in" onClick={handleClose}>Logare</NavLink>
                <NavLink to="/auth/register" className="button registration" onClick={handleClose}>Înregistrează-te</NavLink>
              </>
            )}
          </div>
          <div className="burger_menu">
            {items.map((e) => {
              if (e.href) {
                return (
                  <NavLink key={e.id + e.label} to={e.href} onClick={handleClose} className="menu_item">
                    {e.label}
                  </NavLink>
                );
              }
              return (
                <Fragment key={e.id + e.label}>
                  {isAuthenticated ? (
                    <Collapse width="100%" title={e.label} className="products">
                      {e.items && e.items.map(({ id, title, ...i }) => (
                        <div key={id + title.first} className="products__submenu_item" onClick={handleClose}>
                          <NavLink
                            key={id}
                            to={i.key === ProductsEnum.requests ? '/profile/request' : i.key === ProductsEnum.legacy ? `/legacy/${categories[0].name}` : `/products/${i.key}`}
                            className="menu_item products__submenu_item_title"
                          >
                            {title.first} {title.second}
                          </NavLink>
                        </div>
                      ))}
                    </Collapse>
                  ) : null}
                </Fragment>
              );
            })}
          </div>
        </Drawer.Content>
      </Drawer>
    </div>
  );
};

let timeout: NodeJS.Timeout;

export default function Header() {
  const { isSm } = useIsMobile();
  const { pathname } = useLocation();
  const { isAuthenticated, logout } = useAuth();
  const [items] = useState(headerItems(isAuthenticated));
  const [activeSubKey, setActiveSubKey] = useState('');

  useEffect(() => {
    if (pathname) {
      const paths = pathname.split('/');
      if (paths[1] === ProductsEnum.legacy) {
        setActiveSubKey(ProductsEnum.legacy);
      } else if (paths[2] && Object.values(ProductsEnum).includes(String(paths[2]) as ProductsEnum)) {
        setActiveSubKey(paths[2]);
      } else {
        setActiveSubKey('');
      }
    }
  }, [pathname]);

  const hoverSubmenu = useCallback((open: boolean) => {
    clearTimeout(timeout);
    const submenu = document.getElementsByClassName('header__submenu')[0] as HTMLElement;
    if (submenu) {
      if (open) {
        submenu.style.opacity = '1';
        submenu.style.visibility = 'visible';
      } else {
        timeout = setTimeout(() => submenu.removeAttribute('style'), 50);
      }
    }
  }, []);

  return (
    <header className="header">
      <EUBanner />
      <div className="container">
        <div className="header__wrapper">
          <HeaderPart />
          <div className="header__part">
            {isSm ? (
              <MobileMenu items={items} />
            ) : (
              <div className="header__part-wrapper">
                <div className="header__part-menu">
                  {items.map((e) =>
                    e.href ? (
                      <NavLink key={e.id + e.label} to={e.href} className="header__left-link">
                        {e.label}
                      </NavLink>
                    ) : (
                      <div
                        key={e.id}
                        className={cn({ 'header__link-submenu': e.items?.length })}
                        {...(e.items?.length ? { onMouseEnter: () => hoverSubmenu(true), onMouseLeave: () => hoverSubmenu(false) } : {})}
                      >
                        <span className="header__left-link">{e.label}</span>
                        {e.items && e.items.length ? (
                          <div className="header__submenu">
                            <div className="container">
                              <div className="header__submenu-box">
                                {e.items.map(({ title: { first, second }, icon, ...i }) => (
                                  <NavLink
                                    key={i.id + first}
                                    to={i.key === ProductsEnum.requests ? '/profile/request' : i.key === ProductsEnum.legacy ? `/legacy/${categories[0].name}` : `/products/${i.key}`}
                                    className="header__submenu-item"
                                    onClick={() => hoverSubmenu(false)}
                                  >
                                    <img className="header__submenu-item-image" src={`/assets/products/${icon}`} alt={first + second} />
                                    <div className="header__submenu-item-title">
                                      <Text>{first}</Text>
                                      <Text>{second}</Text>
                                    </div>
                                    <div className={cn('header__submenu-item-shadow', { 'header__submenu-item-shadow--active': activeSubKey === i.key })} />
                                  </NavLink>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : <></>}
                      </div>
                    ),
                  )}
                </div>
                <div className="header__part-menu header__part-menu--right">
                  {isAuthenticated ? (
                    <>
                      <NavLink to="/profile" className="header__profile-btn">
                        <img src="/assets/icons/profil_icon.svg" alt="profile icon" />
                      </NavLink>
                      <button type="button" className="button" onClick={logout}>Deconectare</button>
                    </>
                  ) : (
                    <>
                      <NavLink to="/auth/login" className="header__login-link">Logare</NavLink>
                      <NavLink to="/auth/register" className="button">Înregistrează-te</NavLink>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}